import React, { useState } from "react";
import { Mail, Phone, MapPin, Send, Instagram, Facebook } from "lucide-react";
import { useNotification } from "../../../contexts/NotificationContext";

const gradientClass =
  "bg-gradient-to-b from-[#484139] via-[#544C44] via-[#5D554C] via-[#655E54] to-[#6B5B4F]";

const ContactUs = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showSuccess, showError } = useNotification();

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const isFormValid =
    formData.name.trim() !== "" &&
    formData.email.trim() !== "" &&
    formData.message.trim() !== "";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await new Promise((r) => setTimeout(r, 800));
      showSuccess("Message sent successfully! We'll get back to you soon.", 5000);
      setFormData({ name: "", email: "", message: "" });
    } catch {
      showError("Failed to send message. Please try again.", 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`${gradientClass} min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8`}>
      <div
        className="w-full max-w-5xl rounded-2xl sm:rounded-3xl p-5 sm:p-8 lg:p-12 shadow-[0_15px_60px_-15px_rgba(0,0,0,0.5)] border border-white/10"
        style={{
          backgroundImage:
            "radial-gradient(at 50% 50%, rgba(255, 255, 255, 0.05) 0%, rgba(53, 49, 44, 0.9) 100%)",
          backdropFilter: "blur(4px)",
        }}
      >
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-center mb-2 text-white">
          Let's Talk
        </h1>
        <p className="text-center mb-8 sm:mb-10 text-base sm:text-lg lg:text-xl font-light text-[#E3D5C5]">
          We're here to help you find your perfect skincare match.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Form */}
          <div className="order-2 lg:order-1">
            <h2 className="text-2xl sm:text-3xl font-serif font-bold mb-5 sm:mb-6 text-white">
              Send us a Message
            </h2>

            <form className="space-y-4 sm:space-y-5" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-1.5 text-[#E3D5C5]">
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your name"
                  className="w-full p-3 sm:p-4 rounded-xl border-2 border-white/10 bg-white/5 placeholder-gray-400 text-white focus:outline-none focus:ring-1 focus:ring-[#DDBB92] focus:border-[#DDBB92] transition"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-1.5 text-[#E3D5C5]">
                  Your Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  className="w-full p-3 sm:p-4 rounded-xl border-2 border-white/10 bg-white/5 placeholder-gray-400 text-white focus:outline-none focus:ring-1 focus:ring-[#DDBB92] focus:border-[#DDBB92] transition"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium mb-1.5 text-[#E3D5C5]">
                  Your Message
                </label>
                <textarea
                  id="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Type your message here..."
                  className="w-full p-3 sm:p-4 rounded-xl border-2 border-white/10 bg-white/5 placeholder-gray-400 text-white focus:outline-none focus:ring-1 focus:ring-[#DDBB92] focus:border-[#DDBB92] transition resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={!isFormValid || isSubmitting}
                className={`w-full sm:w-auto inline-flex items-center justify-center px-7 sm:px-8 py-3 rounded-full font-bold text-base sm:text-lg shadow-lg transition ${
                  isFormValid && !isSubmitting
                    ? "bg-[#DDBB92] text-[#484139] hover:bg-opacity-90"
                    : "bg-gray-500/50 text-gray-300 cursor-not-allowed"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <span className="w-5 h-5 mr-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-3" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Info */}
          <div className="order-1 lg:order-2 flex flex-col justify-center space-y-6 sm:space-y-8 p-5 sm:p-6 bg-[#35312C] rounded-xl shadow-xl">
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-white mb-1">
              Contact Details
            </h2>

            <div className="flex items-start gap-3 sm:gap-4">
              <MapPin className="w-5 h-5 sm:w-6 sm:h-6 mt-1 text-[#DDBB92] flex-shrink-0" />
              <div>
                <p className="font-bold text-base sm:text-lg text-white">Our Location</p>
                <p className="text-sm sm:text-base text-[#E3D5C5]">
                  Glowy HQ, 123 Radiant Avenue,
                  <br />
                  Skincare City, CA 90210
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 sm:gap-4">
              <Mail className="w-5 h-5 sm:w-6 sm:h-6 mt-1 text-[#DDBB92] flex-shrink-0" />
              <div>
                <p className="font-bold text-base sm:text-lg text-white">Email Us</p>
                <a
                  href="mailto:support@glowy.com"
                  className="text-sm sm:text-base text-[#E3D5C5] hover:text-white transition break-all"
                >
                  support@glowy.com
                </a>
              </div>
            </div>

            <div className="flex items-start gap-3 sm:gap-4">
              <Phone className="w-5 h-5 sm:w-6 sm:h-6 mt-1 text-[#DDBB92] flex-shrink-0" />
              <div>
                <p className="font-bold text-base sm:text-lg text-white">Call Us</p>
                <a
                  href="tel:+1234567890"
                  className="text-sm sm:text-base text-[#E3D5C5] hover:text-white transition"
                >
                  +1 (234) 567-890
                </a>
              </div>
            </div>

            <div className="pt-4 border-t border-white/10">
              <p className="font-bold text-base sm:text-lg text-white mb-3">Connect With Us</p>
              <div className="flex gap-4">
                <a href="https://instagram.com" aria-label="Instagram" target="_blank" rel="noreferrer" className="text-white hover:text-[#DDBB92] transition">
                  <Instagram className="w-6 h-6" />
                </a>
                <a href="https://facebook.com" aria-label="Facebook" target="_blank" rel="noreferrer" className="text-white hover:text-[#DDBB92] transition">
                  <Facebook className="w-6 h-6" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
